import { parseChildrenIds } from '@car-pool/shared/schoolRecords';

function numericId(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function compareByName(left, right) {
  const leftName = `${left?.l_name || ''} ${left?.f_name || ''}`.trim().toLowerCase();
  const rightName = `${right?.l_name || ''} ${right?.f_name || ''}`.trim().toLowerCase();
  return leftName.localeCompare(rightName);
}

function parentAccountId(driver) {
  const match = String(driver?.emergency_backup_phone || '').match(/^PARENT_ACCOUNT:(\d+)$/i);
  return match ? numericId(match[1]) : null;
}

function studentAuthorizedContactIds(student) {
  return parseChildrenIds(
    student?.guardians_permanent_authorized_driver
      ?? student?.['guardians/permanent_authorized_driver']
      ?? []
  );
}

// A record that has been withdrawn or refused is history, not permission.
const LIVE_AUTHORIZATION_STATUSES = new Set(['pending', 'accepted']);

export function isLiveAuthorization(record) {
  return LIVE_AUTHORIZATION_STATUSES.has(String(record?.status || '').toLowerCase());
}

/**
 * Everyone who can collect a child at this school, not only `driver` accounts.
 *
 * A parent or guardian holding an authorization for a child who is not their
 * own is doing exactly what a driver does, so they belong in this directory —
 * the app's parent-to-parent requests create precisely those records, and an
 * administrator has to be able to see and manage them.
 */
function collectAuthorizedContacts(contactList, authorizationList) {
  const ownChildrenByContactId = new Map(
    contactList.map((contact) => [numericId(contact.id), new Set(parseChildrenIds(contact.children_ids).map(numericId))])
  );
  const authorizedContactIds = new Set(
    authorizationList
      .filter((record) => {
        const contactId = numericId(record.authorized_contact_id);
        const studentId = numericId(record.student_id);
        // Their own child needs no authorization; such a row says nothing about
        // them being a driver for anyone.
        return contactId && !ownChildrenByContactId.get(contactId)?.has(studentId);
      })
      .map((record) => numericId(record.authorized_contact_id))
      .filter(Boolean)
  );

  return contactList.filter((contact) => (
    contact.type === 'driver' || authorizedContactIds.has(numericId(contact.id))
  ));
}

export function buildDriverDirectory(contacts = [], students = [], authorizations = []) {
  const contactList = Array.isArray(contacts) ? contacts : [];
  const studentList = Array.isArray(students) ? students : [];
  const authorizationList = Array.isArray(authorizations) ? authorizations : [];
  const contactsById = new Map(contactList.map((contact) => [numericId(contact.id), contact]));
  const studentsById = new Map(studentList.map((student) => [numericId(student.id), student]));
  const parentContacts = contactList.filter((contact) => ['parent', 'guardian'].includes(contact.type));

  return collectAuthorizedContacts(contactList, authorizationList)
    .map((driver) => {
      const driverId = numericId(driver.id);
      const driverAuthorizations = authorizationList
        .filter((record) => numericId(record.authorized_contact_id) === driverId)
        .sort((left, right) => String(right.updated_at || right.created_at || '').localeCompare(String(left.updated_at || left.created_at || '')));

      // `children_ids` means two different things depending on the account. On a
      // driver it is a permanent pickup link; on a parent or guardian it is
      // their own family, which is not an authorization and is never managed
      // from this screen.
      const ownChildrenIds = new Set(parseChildrenIds(driver.children_ids).map(numericId).filter(Boolean));
      const linkedStudentIds = driver.type === 'driver' ? new Set(ownChildrenIds) : new Set();
      driverAuthorizations.forEach((record) => {
        const studentId = numericId(record.student_id);
        if (studentId) linkedStudentIds.add(studentId);
      });
      studentList.forEach((student) => {
        if (studentAuthorizedContactIds(student).map(Number).includes(driverId)) {
          linkedStudentIds.add(numericId(student.id));
        }
      });

      const authorizingContactIds = new Set(
        driverAuthorizations.map((record) => numericId(record.authorizing_contact_id)).filter(Boolean)
      );
      const markedParentId = parentAccountId(driver);
      if (markedParentId) authorizingContactIds.add(markedParentId);

      parentContacts.forEach((parent) => {
        if (parseChildrenIds(parent.children_ids).some((studentId) => linkedStudentIds.has(numericId(studentId)))) {
          authorizingContactIds.add(numericId(parent.id));
        }
      });

      return {
        driver,
        ownChildrenIds: driver.type === 'driver' ? new Set() : ownChildrenIds,
        authorizations: driverAuthorizations,
        students: [...linkedStudentIds]
          .map((studentId) => studentsById.get(studentId))
          .filter(Boolean)
          .sort(compareByName),
        parents: [...authorizingContactIds]
          .map((contactId) => contactsById.get(contactId))
          .filter((contact) => contact && ['parent', 'guardian'].includes(contact.type))
          .sort(compareByName),
      };
    })
    .filter((entry) => entry.authorizations.length || entry.students.length || entry.parents.length)
    .sort((left, right) => compareByName(left.driver, right.driver));
}

/**
 * One row per child this person may collect, with where the permission comes
 * from — which is what decides whether an administrator can edit it here.
 *
 * `temporary` rows are `temporary_authorized_drivers` records: the driver-to-
 * child authorization, editable and revocable from this screen. `permanent`
 * rows come from the student's own record or the driver's standing link; they
 * are shown for completeness and are deliberately not editable here, because
 * changing them would touch the family's own relationship to the child.
 */
export function buildAuthorizedChildRows(entry, students = []) {
  if (!entry) return [];

  const studentsById = new Map(
    [...(entry.students || []), ...(Array.isArray(students) ? students : [])]
      .map((student) => [numericId(student.id), student])
  );
  const rows = new Map();

  (entry.authorizations || []).forEach((record) => {
    const studentId = numericId(record.student_id);
    if (!studentId) return;
    const existing = rows.get(studentId);
    // Several records can name the same child over time; the live one is the
    // permission, so it wins over an expired or revoked predecessor.
    if (existing?.record && isLiveAuthorization(existing.record) && !isLiveAuthorization(record)) return;
    rows.set(studentId, {
      studentId,
      student: studentsById.get(studentId) || null,
      record,
      source: 'temporary',
      editable: true,
      live: isLiveAuthorization(record),
    });
  });

  (entry.students || []).forEach((student) => {
    const studentId = numericId(student.id);
    if (!studentId || rows.has(studentId)) return;
    rows.set(studentId, {
      studentId,
      student,
      record: null,
      source: 'permanent',
      editable: false,
      live: true,
    });
  });

  return [...rows.values()].sort((left, right) => {
    if (left.live !== right.live) return left.live ? -1 : 1;
    return compareByName(left.student, right.student);
  });
}

export function filterDriverDirectory(entries = [], query = '') {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return entries;

  return entries.filter(({ driver, parents, students, authorizations }) => {
    const fields = [
      driver.id,
      driver.f_name,
      driver.l_name,
      [driver.f_name, driver.l_name].filter(Boolean).join(' '),
      driver.email,
      driver.phone,
      driver.emergency_relationship,
      driver.vehicle_make_model,
      driver.car_color,
      driver.license_plate,
      ...parents.flatMap((parent) => [
        parent.f_name,
        parent.l_name,
        [parent.f_name, parent.l_name].filter(Boolean).join(' '),
        parent.email,
        parent.phone,
      ]),
      ...students.flatMap((student) => [
        student.f_name,
        student.l_name,
        [student.f_name, student.l_name].filter(Boolean).join(' '),
        student.grade,
        student.sis_student_id,
      ]),
      ...authorizations.flatMap((record) => [record.status, record.notes]),
    ];

    return fields.some((field) => String(field ?? '').toLowerCase().includes(normalizedQuery));
  });
}

export function getDriverAuthorizationStatus(entry, today = new Date()) {
  const todayValue = today.toISOString().slice(0, 10);
  const records = entry?.authorizations || [];
  const isCurrent = (record) => !record.end_date || String(record.end_date).slice(0, 10) >= todayValue;

  if (records.some((record) => record.status === 'accepted' && isCurrent(record))) {
    return { label: 'Approved', tone: 'approved' };
  }
  if (records.some((record) => record.status === 'pending' && isCurrent(record))) {
    return { label: 'Pending', tone: 'pending' };
  }
  if (records.length) {
    return { label: 'Inactive', tone: 'inactive' };
  }
  if (entry?.students?.length || entry?.parents?.length) {
    return { label: 'Permanent driver', tone: 'permanent' };
  }
  return { label: 'Unassigned', tone: 'unassigned' };
}
